import {ChangeEvent, ClipboardEvent, useState} from 'react';

import {HookSetState} from 'util/Types';

import {Barcodes} from './BarcodeConstants';

export interface TextareaProps {
  value: string;
  onChange: (e: ChangeEvent<HTMLTextAreaElement>) => void;
  onPaste: (e: ClipboardEvent<HTMLTextAreaElement>) => void;
}
export type DeleteBarcode = (barcode: string) => void;
type UseBarcodesResult = [Barcodes, TextareaProps, DeleteBarcode];

function maybeSetNewBarcodes(newText: string, setBarcodes: HookSetState<Barcodes>): void {
  const potentiallyNewValues = newText.split('\n').filter((value: string) => Boolean(value));
  setBarcodes((currentBarcodes) => {
    const anyNew = potentiallyNewValues.some((value) => !currentBarcodes.has(value));
    if (anyNew) {
      const newBarcodes = new Set(currentBarcodes);
      for (const value of potentiallyNewValues) {
        // Search by barcodes is case-insensitive, so make all barcodes lower case for simplicity
        // Trim extra leading/trailing whitespace
        newBarcodes.add(value.toLowerCase().trim());
      }
      return newBarcodes;
    } else {
      return currentBarcodes;
    }
  });
}

/**
 * React hook to extract newline-separated barcodes from a textarea component.
 * Can be used with typing, pasting, or scanning. `barcodes` is updated whenever new barcodes are detected.
 * Skips duplicates and empty lines.
 *
 * See useBarcodes.stories.tsx for example usage.
 */
export default function useBarcodes(): UseBarcodesResult {
  const [text, setText] = useState('');
  const [barcodes, setBarcodes] = useState<Barcodes>(new Set());

  const onChange = (e: ChangeEvent<HTMLTextAreaElement>): void => {
    const newText = e.target.value;
    if (newText[newText.length - 1] === '\n') {
      maybeSetNewBarcodes(newText, setBarcodes);
      // Clear text after receiving new barcodes.
      setText('');
    } else {
      setText(newText);
    }
  };

  const onPaste = (e: ClipboardEvent): void => {
    // Prevent onChange from also firing
    e.preventDefault();

    const pastedText = e.clipboardData.getData('text');
    maybeSetNewBarcodes(pastedText, setBarcodes);
    setText('');
  };

  const deleteBarcode = (barcode: string): void => {
    setBarcodes((currentBarcodes) => {
      if (currentBarcodes.has(barcode)) {
        const newBarcodes = new Set(currentBarcodes);
        newBarcodes.delete(barcode);
        return newBarcodes;
      } else {
        return currentBarcodes;
      }
    });
  };

  return [barcodes, {value: text, onChange, onPaste}, deleteBarcode];
}
